/**
 * Плитки фото карточки МП.
 *
 * Пропорция 3:4 — та же, в которой рендер отдаёт визуалы и в которой их
 * показывает витрина. Фиксировать высоту и обрезать по `cover` нельзя: у
 * вертикального изображения при этом срезается верх и низ, и по превью уже не
 * понять, что именно уедет покупателю.
 */

import React from 'react'
import { SafeImg } from './parts'

interface Props {
  urls: string[]
  article: string
  /** Сколько плиток показывать до раскрытия; остальное прячется за счётчиком. */
  limit?: number
  size?: 'md' | 'sm'
  onOpen: (urls: string[], index: number, article: string) => void
}

const SIZE = {
  md: { hero: 'w-[8.375rem]', tile: 'w-[7.375rem]' },
  sm: { hero: 'w-[5.25rem]', tile: 'w-[5.25rem]' },
}

export const MarketplacePhotos: React.FC<Props> = ({
  urls,
  article,
  limit = 6,
  size = 'md',
  onOpen,
}) => {
  const [expanded, setExpanded] = React.useState(false)

  if (urls.length === 0) {
    return (
      <div className="flex aspect-[3/4] w-[8.375rem] items-center justify-center rounded-lg border border-dashed border-red-300 px-2 text-center text-xs text-red-600 dark:border-red-900 dark:text-red-400">
        Изображений нет
      </div>
    )
  }

  // Счётчик остатка занимает слот в ряду, поэтому при переполнении показываем
  // на одну плитку меньше — иначе ряд переносится ради одной кнопки.
  const overflow = urls.length > limit
  const shown = expanded ? urls : urls.slice(0, overflow ? limit - 1 : limit)
  const rest = urls.length - shown.length

  return (
    <div className="flex flex-wrap items-start gap-2">
      {shown.map((url, index) => (
        <button
          key={url}
          type="button"
          title="Открыть в полном размере"
          onClick={() => onOpen(urls, index, article)}
          className={`relative aspect-[3/4] shrink-0 cursor-zoom-in overflow-hidden rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-indigo-400 dark:border-zinc-700 dark:bg-zinc-900 ${
            index === 0 ? SIZE[size].hero : SIZE[size].tile
          }`}
        >
          <SafeImg
            src={url}
            alt={`${article} — фото ${index + 1}`}
            className="h-full w-full object-cover"
            iconClass="h-4 w-4"
          />
          {index === 0 ? (
            <span className="absolute left-1 top-1 rounded bg-zinc-900/80 px-1.5 py-0.5 text-2xs uppercase leading-normal tracking-wide text-white">
              главное
            </span>
          ) : (
            <span className="absolute bottom-1 right-1 rounded bg-zinc-900/70 px-1 text-2xs leading-normal text-white">
              {index + 1}
            </span>
          )}
        </button>
      ))}

      {(rest > 0 || (expanded && overflow)) && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className={`flex aspect-[3/4] shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-gray-300 text-sm font-semibold text-gray-600 hover:border-indigo-400 hover:text-indigo-600 dark:border-zinc-600 dark:text-zinc-300 ${SIZE[size].tile}`}
        >
          {expanded ? '↑' : `+${rest}`}
          <span className="text-2xs font-normal text-gray-500 dark:text-zinc-400">
            {expanded ? 'свернуть' : 'показать все'}
          </span>
        </button>
      )}
    </div>
  )
}

export default MarketplacePhotos
